/** Small formatting + status-grouping helpers shared across portal pages. */

export const formatDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  // new Date('garbage') doesn't throw — it yields an Invalid Date whose
  // toLocaleDateString() is the literal string "Invalid Date". Guard for it.
  if (isNaN(dt.getTime())) return '';
  return dt.toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
};

export const formatMoney = (v) => {
  if (v === null || v === undefined || v === '' || isNaN(Number(v))) return '';
  return `₹ ${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
};

// Status groupings from the BE note (§3/§4/§5).
export const ORDER_GROUPS = {
  active: ['confirmed', 'delivery_assigned', 'loading', 'loaded', 'in_transit'],
  past: ['delivered', 'invoiced'],
};

export const PO_GROUPS = {
  active: ['confirmed', 'partial_advice_created', 'advice_created', 'loading', 'loaded', 'in_progress'],
  past: ['received', 'completed'],
};

export const INQUIRY_STATUSES = ['pending', 'approved', 'rejected', 'converted'];

/**
 * Loading being finished is what makes the actual (weighed / counted) figures real —
 * they are captured when the challan is generated off the loaded truck. Up to and
 * including `loading` the line only carries what was ordered.
 *
 * Two lifecycles, so two sets: 'order' is a customer purchase (company sales order),
 * 'po' is a customer sale (company purchase order).
 */
const LOADING_DONE_STATUSES = {
  order: ['loaded', 'in_transit', 'delivered', 'invoiced'],
  po: ['loaded', 'in_progress', 'received', 'completed'],
};

export const isLoadingDone = (status, flow = 'order') =>
  (LOADING_DONE_STATUSES[flow] || []).includes(String(status || '').toLowerCase());

/**
 * Render one measure (pieces, or weight/length) for display.
 *
 * Returns the em dash for null/undefined ONLY. A real 0 is shown as 0 — after loading
 * that means "nothing went on the truck for this line", which the customer should see
 * rather than have hidden. Crucially there is no cross-fallback: an absent actual never
 * borrows the ordered value, which is how the printed-challan helper gets it wrong
 * (`actual_weight ?? actual_quantity ?? orderQtyValue`).
 */
/**
 * The actual measured weight/length for a line, or null when nothing was captured.
 *
 * `OrderItem.actual_quantity` (and `PurchaseAdviceItem.actual_quantity` behind the sales
 * tab) default to 0 in the schema, so 0 doubles as "never captured" and cannot be told
 * apart from a genuine zero. Showing "0 kg" would assert a measurement that may never
 * have been taken, so a zero is reported as absent and the cell shows a dash.
 *
 * Candidates are tried in order and only ACTUAL fields belong here — passing the ordered
 * quantity in would reintroduce exactly the fallback this is meant to prevent.
 */
export const actualMeasure = (...candidates) => {
  for (const value of candidates) {
    if (value === null || value === undefined || value === '') continue;
    const num = Number(value);
    if (Number.isNaN(num) || num === 0) continue;
    return num;
  }
  return null;
};

export const formatMeasure = (value, unit = '') => {
  if (value === null || value === undefined || value === '') return '—';
  const num = Number(value);
  if (Number.isNaN(num)) return '—';
  const text = num.toLocaleString('en-IN', { maximumFractionDigits: 2 });
  return unit ? `${text} ${unit}` : text;
};

/**
 * A single list endpoint takes one `status` value. For an Active/Past tab that
 * spans several statuses, we fetch the union client-side by calling once per
 * status and merging. This helper builds the per-status param list.
 */
export const statusesForGroup = (groups, group) => {
  if (group === 'all' || !group) return [undefined]; // one call, no status filter
  return groups[group] || [undefined];
};
